<template>
  <div class="doctor-day-timeline" data-testid="doctor-timeline">
    <!-- header / collapsed bar (the whole bar is the toggle) -->
    <button
      type="button"
      class="dt-bar"
      data-testid="doctor-timeline-toggle"
      :aria-expanded="(!collapsed).toString()"
      @click="toggle"
    >
      <span class="dt-title">👨‍⚕️ แพทย์{{ dayLabel }} ({{ totalCount }})</span>
      <span v-if="now" class="dt-onduty">● ออกตรวจตอนนี้ {{ onDutyCount }}</span>
      <span v-if="leaveCount" class="dt-leave">| ลา {{ leaveCount }}</span>

      <span v-if="collapsed && !isEmpty" class="dt-spark" aria-hidden="true">
        <i
          v-for="(b, i) in coverageBuckets"
          :key="i"
          class="dt-spark-bar"
          :style="{ height: sparkHeight(b) }"
        ></i>
      </span>

      <span class="dt-spacer"></span>
      <span class="dt-toggle">{{ collapsed ? "▾ ดูตารางแพทย์" : "▴ ย่อ" }}</span>
    </button>

    <!-- expanded body -->
    <div v-if="!collapsed" class="dt-body" data-testid="doctor-timeline-body">
      <div v-if="loading" class="dt-msg">กำลังโหลด…</div>
      <div
        v-else-if="isEmpty"
        class="dt-msg"
        data-testid="doctor-timeline-empty"
      >
        ยังไม่มีแพทย์ลงเวลาใน{{ dayLabel }}
      </div>
      <template v-else>
        <div class="dt-axis">
          <span v-for="(t, i) in axisTicks" :key="i">{{ t }}</span>
        </div>
        <div class="dt-scroll">
          <div v-if="now" class="dt-now" :style="{ left: nowLeft }"></div>
          <div
            v-for="row in rows"
            :key="row.branchMemberId"
            class="dt-row"
            :class="{ 'is-leave': row.isLeaveWhole }"
          >
            <span class="dt-name" :title="rowTooltip(row)">
              {{ row.name }}<span v-if="row.isLeaveWhole"> (ลา)</span>
            </span>
            <span class="dt-track">
              <i
                v-if="row.isLeaveWhole"
                class="dt-seg dt-seg-leave"
                :title="rowTooltip(row)"
              ></i>
              <i
                v-for="(seg, i) in row.segments"
                v-else
                :key="i"
                class="dt-seg"
                :style="{
                  left: seg.leftPct + '%',
                  width: seg.widthPct + '%',
                  background: row.color,
                }"
                :title="rowTooltip(row)"
              ></i>
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  buildTimeAxis,
  buildDoctorRows,
  buildCoverageBuckets,
  countOnDutyAt,
  timeToPct,
  parseTimeToMinutes,
  minutesToLabel,
} from "@/utils/timelineLayout";

const LS_KEY = "dc.queueManage.doctorTimeline.collapsed";
const NAME_COL_PX = 88;

export default {
  name: "DoctorDayTimeline",
  props: {
    date: { type: String, default: "" },
    members: { type: Array, default: () => [] },
    doctors: { type: Array, default: () => [] },
    openingTime: { type: String, default: "09:00" },
    closingTime: { type: String, default: "18:00" },
    now: { type: String, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { collapsed: this.readCollapsed() };
  },
  computed: {
    axis() {
      return buildTimeAxis(this.members, {
        openingTime: this.openingTime,
        closingTime: this.closingTime,
      });
    },
    rows() {
      return buildDoctorRows(this.members, this.doctors, this.axis);
    },
    coverageBuckets() {
      return buildCoverageBuckets(this.members, this.axis, 16);
    },
    maxCoverage() {
      return Math.max(1, ...this.coverageBuckets);
    },
    totalCount() {
      return this.rows.length;
    },
    leaveCount() {
      return this.rows.filter(r => r.isLeaveWhole).length;
    },
    onDutyCount() {
      return this.now ? countOnDutyAt(this.members, this.now) : 0;
    },
    isEmpty() {
      return !this.loading && this.rows.length === 0;
    },
    dateText() {
      if (!this.date) return "";
      const [y, m, d] = this.date.split("-");
      return `${d}/${m}/${y}`;
    },
    dayLabel() {
      // `now` is non-null only when the parent shows today (QueueManage's
      // timelineNow), so it doubles as the "is today" signal — no clock read here.
      return this.now ? "วันนี้" : `วันที่ ${this.dateText}`;
    },
    axisTicks() {
      const a = this.axis;
      const ticks = [];
      for (let i = 0; i <= 4; i++) {
        const min = a.startMin + ((a.endMin - a.startMin) * i) / 4;
        ticks.push(minutesToLabel(Math.round(min)));
      }
      return ticks;
    },
    nowLeft() {
      if (!this.now) return "0";
      const pct = timeToPct(parseTimeToMinutes(this.now), this.axis) / 100;
      return `calc(${NAME_COL_PX}px + (100% - ${NAME_COL_PX}px) * ${pct})`;
    },
  },
  methods: {
    readCollapsed() {
      try {
        const v = window.localStorage.getItem(LS_KEY);
        return v === null ? true : v === "true";
      } catch (e) {
        return true;
      }
    },
    toggle() {
      this.collapsed = !this.collapsed;
      try {
        window.localStorage.setItem(LS_KEY, String(this.collapsed));
      } catch (e) {
        /* storage disabled — keep in-memory state only */
      }
    },
    sparkHeight(count) {
      const pct = Math.round((count / this.maxCoverage) * 100);
      return `${Math.max(12, pct)}%`;
    },
    rowTooltip(row) {
      const parts = [`${row.name} · ${row.ranges}`];
      if (row.rooms && row.rooms.length) {
        parts.push(`ห้อง ${row.rooms.join(", ")}`);
      }
      return parts.join(" · ");
    },
  },
};
</script>

<style scoped>
.doctor-day-timeline {
  font-size: 12px;
  margin: 6px 4px 0;
}
.dt-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: #f0f5fb;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
}
.dt-title {
  font-weight: 600;
  color: #2b6cb0;
}
.dt-onduty {
  color: #2f855a;
}
.dt-leave {
  color: #a0aec0;
}
.dt-spacer {
  flex: 1;
}
.dt-toggle {
  color: #2b6cb0;
  font-weight: 600;
  white-space: nowrap;
}
.dt-spark {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 16px;
}
.dt-spark-bar {
  display: block;
  width: 5px;
  background: #7aa7d6;
  border-radius: 1px;
}
.dt-body {
  position: relative;
  background: #fafbfc;
  border: 1px solid #cbd5e0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  padding: 8px 10px;
}
.dt-msg {
  color: #718096;
  padding: 6px 0;
  text-align: center;
}
.dt-axis {
  display: flex;
  justify-content: space-between;
  color: #a0aec0;
  font-size: 10px;
  margin-left: 88px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 2px;
  margin-bottom: 4px;
}
.dt-scroll {
  position: relative;
  max-height: 168px; /* ~6 rows then scroll; never pushes the grid down */
  overflow-y: auto;
}
.dt-now {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e53e3e;
  opacity: 0.6;
  pointer-events: none;
}
.dt-row {
  display: flex;
  align-items: center;
  margin: 3px 0;
}
.dt-row.is-leave {
  color: #b7bcc2;
}
.dt-name {
  width: 88px;
  flex: 0 0 88px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 6px;
}
.dt-track {
  position: relative;
  flex: 1;
  height: 10px;
  background: #eef2f6;
  border-radius: 4px;
}
.dt-seg {
  position: absolute;
  top: 0;
  height: 10px;
  border-radius: 4px;
}
.dt-seg-leave {
  left: 0;
  width: 100%;
  background: repeating-linear-gradient(
    45deg,
    #f0f0f0,
    #f0f0f0 4px,
    #e2e2e2 4px,
    #e2e2e2 8px
  );
}
</style>
